import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

import { useUserContext } from 'App/contexts/UserContext';
import { saveEhrNote } from 'App/services/AMSService';
import { queryClient } from 'Shared/queryClient';
import { branchSettingsPropType, patientPropType } from 'Shared/types';
import FormButtonGroup from '../../styles/FormButtonGroup';
import useFetchMedicationAssociations from '../hooks/useFetchMedicationAssociations';
import { activityPropType, questionGroupPropType, questionPropType } from '../types';
import EhrEncounterPrescriberContent from './EhrEncounterPrescriberContent';
import EhrEncounterReviewContent from './EhrEncounterReviewContent';
import EhrEncounterVisitContent from './EhrEncounterVisitContent';
import LaddaButton from 'App/components/LaddaButton';

const ehrModalAction = Object.freeze({
    review: 'review',
    associateVisit: 'associateVisit',
    associatePrescriber: 'associatePrescriber',
});

const EhrEncounterNoteModal = ({
    activity,
    patient,
    allQuestions,
    questionGroups,
    branchSettings,
    onClose,
    onComplete,
}) => {
    const { values, setFieldTouched, validateForm, isValid } = useFormikContext();
    const user = useUserContext();
    const { data: medicationAssociations } = useFetchMedicationAssociations(patient.id, activity.id);
    const medicationAssociationsList = !medicationAssociations ? [] : Object.values(medicationAssociations);
    const ehrMessage = useRef();

    const [modalState, setModalState] = useState(ehrModalAction.review);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const modalStateToTitle = {
        [ehrModalAction.review]: activity.name,
        [ehrModalAction.associateVisit]: 'Associate Visit',
        [ehrModalAction.associatePrescriber]: 'Provider',
    };
    const modalStateToComponent = {
        [ehrModalAction.review]: (
            <EhrEncounterReviewContent
                patient={patient}
                allQuestions={allQuestions}
                questionGroups={questionGroups}
                branchSettings={branchSettings}
                medicationAssociationsList={medicationAssociationsList}
            />
        ),
        [ehrModalAction.associateVisit]: <EhrEncounterVisitContent patient={patient} />,
        [ehrModalAction.associatePrescriber]: (
            <EhrEncounterPrescriberContent medicationAssociationsList={medicationAssociationsList} />
        ),
    };
    const requireVisit = branchSettings.ehrNoteRequiresVisit;
    const requirePrescriber =
        branchSettings.redoxNoteRequiresPrescriber && branchSettings.redoxNotePrescriberType === 'prescriber';

    const availableModalStates = [ehrModalAction.review];
    if (requireVisit) {
        availableModalStates.push(ehrModalAction.associateVisit);
    }
    if (requirePrescriber) {
        availableModalStates.push(ehrModalAction.associatePrescriber);
    }
    const currentStateIndex = availableModalStates.findIndex((value) => value === modalState);

    const handleGoBack = () => {
        if (currentStateIndex === 0) {
            onClose();
        }
        setModalState(availableModalStates[currentStateIndex - 1]);
        setImmediate(() => validateForm());
    };

    async function handleSubmitButton(sendToEhr) {
        setIsSubmitting(true);
        if (!isValid) {
            setIsSubmitting(false);
            return;
        }
        if (modalState === ehrModalAction.review) {
            const messageHtml = `<div>${document.getElementsByClassName('modal-header')[0].outerHTML}${
                document.getElementsByClassName('modal-body')[0].outerHTML
            }</div>`;
            ehrMessage.current = messageHtml;
        }
        if (requireVisit && modalState === ehrModalAction.review) {
            setModalState(ehrModalAction.associateVisit);
            setFieldTouched('associatedVisit');
            setImmediate(() => validateForm());
            setIsSubmitting(false);
        } else if (requirePrescriber && modalState !== ehrModalAction.associatePrescriber) {
            setModalState(ehrModalAction.associatePrescriber);
            setFieldTouched('associatedPrescriber');
            setImmediate(() => validateForm());
            setIsSubmitting(false);
        } else {
            const payload = {
                message: ehrMessage.current,
                ehrProviderId: user.user_attributes.ehrProviderId,
                visitId: values.associatedVisit?.visitId,
                facilityId: values.associatedVisit?.facilityId,
                prescriberId: values.associatedPrescriber?.rx?.physician?.id,
                sendToEhr,
            };
            try {
                await onComplete();
                await saveEhrNote(activity.id, payload);
            } finally {
                setIsSubmitting(false);
            }
            queryClient.invalidateQueries(['activity', patient.id, activity.id]);
            onClose();
        }
    }

    return (
        <Modal show>
            <Modal.Header>
                <Modal.Title role="heading">{modalStateToTitle[modalState]}</Modal.Title>
            </Modal.Header>

            <Modal.Body id="ehr-note-body">{modalStateToComponent[modalState]}</Modal.Body>

            <Modal.Footer>
                <FormButtonGroup>
                    <Button variant="outline-danger" disabled={isSubmitting} onClick={() => handleSubmitButton(false)}>
                        Don&apos;t Send to EHR
                    </Button>

                    <div style={{ flexGrow: 1 }} />

                    <Button variant="outline-secondary" disabled={isSubmitting} onClick={handleGoBack}>
                        Go Back
                    </Button>
                    <LaddaButton
                        isLoading={isSubmitting}
                        id="ehr-note-send-btn"
                        variant="primary"
                        onClick={() => handleSubmitButton(true)}
                    >
                        {/* eslint-disable-next-line no-nested-ternary */}
                        {currentStateIndex === availableModalStates.length - 1
                            ? 'Send to EHR'
                            : modalState === ehrModalAction.associateVisit && requirePrescriber
                              ? 'Select Prescriber'
                              : 'Next'}
                    </LaddaButton>
                </FormButtonGroup>
            </Modal.Footer>
        </Modal>
    );
};

EhrEncounterNoteModal.propTypes = {
    activity: activityPropType.isRequired,
    patient: patientPropType.isRequired,
    allQuestions: PropTypes.objectOf(questionPropType).isRequired,
    questionGroups: PropTypes.arrayOf(questionGroupPropType).isRequired,
    branchSettings: branchSettingsPropType.isRequired,
    onClose: PropTypes.func.isRequired,
    onComplete: PropTypes.func.isRequired,
};

export default EhrEncounterNoteModal;
